import React from 'react';
import styled from 'astroturf/react';
import pluralize from 'pluralize';

import { Card, Header, RoundButton as RoundButtonBase } from './common';
import { ResearchInterest } from './hooks/types';

interface Props {
  // Whether to use the alternate clean look (part of swp_clean_rr ab test)
  altLook?: boolean;
  // This acts as a toggle so we don't need two functions
  // This is called whether we're following or unfollowing
  onFollow: (ri: ResearchInterest) => void;
  researchInterests: ResearchInterest[];
}

const Check = styled('i').attrs({
  className: 'fa fa-check',
})`
  color: #777;
  margin-right: 10px;
`;

const Container = styled.div`
  background: #fff;
  border-bottom: 1px solid #ddd;
  cursor: pointer;
  display: grid;
  gap: 2px;
  grid-template-columns: 1fr auto;
  grid-template-rows: auto auto;
  justify-content: center;
  padding: 12px;

  &:hover {
    background: #f8f8f8;
  }

  &.altLook {
    border-bottom: 1px solid transparent;
    position: relative;
  }

  // With altLook, there's a border bottom that doesn't go all the way to the left edge.
  // We can't use a left-margin because we want the hover effect to take
  // up the entire space, so we have to use an after element for the bottom border.
  &.altLook::after {
    background: #ddd;
    bottom: 0;
    content: '';
    left: 12px;
    height: 1px;
    position: absolute;
    width: calc(100% - 12px);
  }
`;

const ContainerBackgroundLink = styled.a`
  position: absolute;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
`;

const Followers = styled.p`
  color: #333;
  font-size: 13px;
  margin: 0;
`;

const Name = styled.a`
  align-self: end;
  color: #4b4b4b;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  margin: 0;
  position: relative;

  &:hover {
    color: #4b4b4b;
    text-decoration: underline;
  }
`;

const Plus = styled('i').attrs({
  className: 'fa fa-plus',
})`
  color: #0854d3;
  margin-right: 10px;
`;

const RoundButton = styled(RoundButtonBase)`
  position: relative;
  align-self: center;
  grid-column: 2 / 3;
  grid-row: 1 / 3;
`;

const ResearchInterestsCard: React.FC<Props> = ({
  altLook,
  onFollow,
  researchInterests,
}: Props) => {
  return (
    <Card altLook={altLook}>
      <Header altLook={altLook}>Popular Research Interests</Header>

      {researchInterests.map((ri) => (
        // @ts-expect-error ts-migrate(2769) FIXME: No overload matches this call.
        <Container altLook={altLook} key={ri.id}>
          <ContainerBackgroundLink href={ri.url} />
          <Name href={ri.url}>{ri.name}</Name>
          <Followers>
            {`${ri.count} ${pluralize('follower', ri.count)}`}
          </Followers>

          <RoundButton gray={ri.following} onClick={() => onFollow(ri)}>
            {ri.following ? <Check /> : <Plus />}
            {ri.following ? 'Following' : 'Follow'}
          </RoundButton>
        </Container>
      ))}
    </Card>
  );
};

export default ResearchInterestsCard;
